import { useAddressLookupStore } from "@/domains/addressLookups/addressLookupStore";
import { useFleetStore } from "@/domains/fleet/fleetStore";
import { useInspectionHistoryStore } from "@/domains/inspectionHistory";
import { useSavedLocationStore } from "@/domains/locations/savedLocationStore";
import { useNotificationsStore } from "@/domains/notifications/notificationsStore";
import { useRucStore } from "@/domains/ruc";
import { useTripStore } from "@/domains/trips";
import { useUserStore } from "@/domains/user/userStore";
import { useVehicleStore } from "@/domains/vehicles";
import { keycloak } from "@/plugins/auth";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useContentStore } from "./contentStore";

type BootstrapStage =
  | "user"
  | "user-validation"
  | "fleet"
  | "fleet-validation"
  | "trips"
  | "complete";

export const useAppStore = defineStore("appStore", () => {
  // ---------------------------------- hooks --------------------------------- //

  const userStore = useUserStore();
  const tripStore = useTripStore();
  const vehicleStore = useVehicleStore();
  const savedLocationsStore = useSavedLocationStore();
  const fleetStore = useFleetStore();
  const addressLookupStore = useAddressLookupStore();
  const contentStore = useContentStore();
  const notificationsStore = useNotificationsStore();
  const inspectionHistoryStore = useInspectionHistoryStore();
  const rucStore = useRucStore();

  // --------------------------------- state ---------------------------------- //

  const initialised = ref<boolean>(false);
  const bootstrapStage = ref<BootstrapStage>("user");
  
  // --------------------------------- getters -------------------------------- //

  const bootstrapStageMsg = computed(() => {
    switch (bootstrapStage.value) {
      case "user":
        return "Getting things ready...";
      case "user-validation":
        return "Checking user data...";
      case "fleet":
        return "Fetching fleet data...";
      case "fleet-validation":
        return "Confirming fleet data...";
      case "trips":
        return "Retrieving planned trips...";
      case "complete":
        return "All done";
    }
  });

  // --------------------------------- actions -------------------------------- //

  async function bootstrap() {
    bootstrapStage.value = "user";

    await userStore.fetchUsers();

    bootstrapStage.value = "user-validation";

    // TODO: implement validation

    if (userStore.usersFetchState === "error") {
      // TODO: implement handle error
      return;
    }

    if (!userStore.getCurrentUser?.inFleetFlag) {
      // TODO: implement the onboarding thing
      return;
    }

    bootstrapStage.value = "fleet";

    await Promise.all([
      userStore.fetchUserPrefs(),
      fleetStore.fetchFleet(),
      contentStore.fetchContent(),
      vehicleStore.fetchVehicles(),
      savedLocationsStore.fetchLocations(),
      notificationsStore.fetchNotifications(),
      rucStore.fetchRuc(),
      inspectionHistoryStore.fetchInspectionHistory(),
      // PoC-ONLY CHOICE — DO NOT PORT AS-IS when this folds back into the
      // main EV app. Fetching every vehicle's location eagerly at login only
      // makes sense here because the PoC has nowhere else that naturally
      // triggers it. The main app should trigger this from wherever a
      // vehicle's location actually gets used (e.g. a map/tracking view
      // opening), not from bootstrap - eagerly fetching live location data
      // for a whole fleet on every login doesn't scale and goes stale the
      // moment the user looks at it. See vehicleStore.fetchVehicleLocations.
      vehicleStore.fetchVehicleLocations(),
    ]);

    // PoC-ONLY CHOICE — DO NOT PORT AS-IS. Kicks off vehicleStore's demo-only
    // location polling (see vehicleStore.startLocationsPolling for the full
    // reasoning: no time to stand up a websocket/push channel, so this
    // mirrors the demo engine's own ~10s update cadence instead). The main
    // app should start/stop this from wherever vehicle locations are
    // actually displayed, not unconditionally for every session.
    vehicleStore.startLocationsPolling();

    bootstrapStage.value = "fleet-validation";

    // TODO: implement validation

    bootstrapStage.value = "trips";

    await tripStore.fetchTrips(vehicleStore.vehicles); 
    bootstrapStage.value = "complete";
    initialised.value = true;
  }

  function logout(): void {
    // clear stores of user related state

    userStore.$reset();
    tripStore.$reset();
    savedLocationsStore.$reset();
    addressLookupStore.$reset();
    vehicleStore.$reset();
    fleetStore.$reset();
    notificationsStore.$reset();
    inspectionHistoryStore.$reset();
    rucStore.$reset();

    // end users session

    keycloak.logout();
  }

  return {

    // --------------------------------- State -------------------------------- //

    /** flag indicating if the application has reached a ready state. */
    initialised,

    // -------------------------------- Getters ------------------------------- //

    /** UI ready message advising the stage the bootstrap process is at. */
    bootstrapStageMsg,
    // -------------------------------- Actions ------------------------------- //

    /** Orchestrates the applications start up process. */
    bootstrap,

    /** Terminates the users session and orchestrates clearing all session related data. */
    logout,
  };
});
